import { useState, type FC, type ReactNode } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@wwe-portal/ui/components/popover';
import { Skeleton, TextSkeleton } from '@wwe-portal/ui/components/skeleton';
import Image from 'next/image';
import { useAuthStore } from '@wwe-portal/web/providers/stores/auth-store';
import { PlayerLink } from '../../common/link/player-link';
import { showDialog } from '@wwe-portal/ui/components/dialog';
import { AvatarSelectDialogPortal } from '../../player-profile/modals/AvatarSelectDialog';
import { useLayoutStore } from '@wwe-portal/web/providers/stores/layout-store';
import { FactionLink } from '../../common/link/faction-link';
import { avatarFrameRender } from '../../factionProfile/FactionPlayerCard';
import { useTranslations } from 'next-intl';
import { TwitchDialog } from './twitch-dialog';

const Layout: FC<{
  avatarSlot: ReactNode;
  displayNameSlot: ReactNode;
  factionSlot: ReactNode;
}> = ({ avatarSlot, displayNameSlot, factionSlot }) => {
  return (
    <div className="flex flex-col items-center gap-2.5">
      <div className="relative flex size-[150px] items-center justify-center">
        {avatarSlot}
      </div>
      <div className="flex flex-col items-center">
        <div className="text-text300 text-base font-medium">
          {displayNameSlot}
        </div>
        <div className="text-secondary text-xs">{factionSlot}</div>
      </div>
    </div>
  );
};

const MenuItem: FC<{
  children: ReactNode;
  onClick?: () => void;
}> = ({ children, onClick }) => {
  return (
    <ClickableDiv
      className="hover:bg-back300 -mx-3 flex h-10 items-center gap-3 rounded-xl px-3"
      onClick={onClick}
    >
      {children}
    </ClickableDiv>
  );
};

export interface UserEditProps {
  avatar?: string;
  frame: string;
  displayName: string;
  faction?: string;
  rarity?: string;
  size?: number;
  isShowAccount?: boolean;
  isProfilePage?: boolean;
}

export const UserEdit: FC<UserEditProps> = ({
  avatar,
  displayName,
  faction,
  frame,
  rarity,
  size = 93,
  isShowAccount = true,
  isProfilePage = false,
}) => {
  const t = useTranslations();
  const setSidebar = useLayoutStore((state) => state.setSidebar);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { logout } = useAuthStore();

  const closeMenuAndSidebar = () => {
    setDropdownOpen(false);
    setSidebar('none');
  };

  const handleLogout = async () => {
    closeMenuAndSidebar();
    logout(true);
  };

  const handleStreamSettings = () => {
    closeMenuAndSidebar();
    showDialog(TwitchDialog);
  };

  return (
    <>
      <Popover open={dropdownOpen}>
        <PopoverTrigger asChild>
          <div className="relative flex size-[200px] items-center justify-center">
            <img
              alt="avatar"
              className="absolute rounded-full object-cover"
              src={avatar ?? '/assets/images/user/default-avatar.webp'}
              style={{ width: size, height: size }}
            />
            {frame ? (
              <img
                alt="frame"
                className="absolute rounded-full object-cover"
                src={frame}
                style={{ width: size + 20, height: size + 20 }}
              />
            ) : avatarFrameRender(rarity) ? (
              <img
                className="absolute"
                src={avatarFrameRender(rarity)}
                style={{ width: size + 30, height: size + 30 }}
              />
            ) : (
              <div
                className="size-[100px] rounded-full border-[2px] border-solid border-[#31365e]"
                style={{ width: size + 20, height: size + 20 }}
              />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="bg-back200 pointer-events-auto w-[240px] rounded-lg px-6 py-4 text-xs"
          onPointerDownOutside={() => {
            setDropdownOpen(false);
          }}
          sideOffset={20}
        >
          <div className="text-text500 flex flex-col">
            <MenuItem
              onClick={() => {
                closeMenuAndSidebar();
                showDialog(AvatarSelectDialogPortal);
              }}
            >
              <Image
                width={18}
                height={18}
                alt="change avatar"
                src="/assets/icons/change-avatar.svg"
              />

              <span>Change Avatar</span>
            </MenuItem>
            {!isProfilePage && (
              <>
                <MenuItem>
                  <Image
                    width={16}
                    height={16}
                    alt="player profile"
                    src="/assets/icons/playerProfile.svg"
                  />
                  <PlayerLink name={displayName} onClick={closeMenuAndSidebar}>
                    <span>View Player Profile</span>
                  </PlayerLink>
                </MenuItem>
                {faction && (
                  <MenuItem>
                    <Image
                      width={16}
                      height={16}
                      alt="faction profile"
                      src="/assets/icons/factionProfile.svg"
                    />
                    <FactionLink name={faction} onClick={closeMenuAndSidebar}>
                      <span>View Faction Profile</span>
                    </FactionLink>
                  </MenuItem>
                )}
                <MenuItem onClick={handleStreamSettings}>
                  <Image
                    width={18}
                    height={18}
                    alt={t('stream_settings')}
                    src="/assets/icons/streamSetting.svg"
                  />
                  <span>{t('stream_settings')}</span>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Image
                    width={18}
                    height={18}
                    alt="logout"
                    src="/assets/icons/logout.svg"
                  />
                  <ClickableDiv>Logout</ClickableDiv>
                </MenuItem>
              </>
            )}
          </div>
          <PopoverArrow
            className="text-back200"
            fill="currentColor"
            height={10}
            width={20}
          />
        </PopoverContent>
      </Popover>
      {isShowAccount && (
        <ClickableDiv
          className="text-text400 bg-back600 absolute -bottom-1 -right-1 rounded-full p-[5px]"
          onClick={() => {
            setDropdownOpen(true);
          }}
        >
          <div className="bg-back200 flex size-7 items-center justify-center rounded-full">
            <svg
              fill="none"
              height="14"
              viewBox="0 0 15 14"
              width="15"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clipRule="evenodd"
                d="M.442 2.844C.442 1.348 1.654.136 3.149.136h6.943c1.495 0 2.707 1.212 2.707 2.708v2.082c0 .345-.28.625-.624.625-.346 0-.625-.28-.625-.625V2.844c0-.806-.653-1.458-1.458-1.458H3.149c-.805 0-1.458.652-1.458 1.458v6.248c0 .805.653 1.458 1.458 1.458h3.472c.345 0 .624.28.624.625s-.28.624-.624.624H3.149C1.654 11.8.442 10.587.442 9.092V2.844zm2.777 1.388c0-.345.28-.625.625-.625h5.554c.345 0 .624.28.624.625s-.28.625-.624.625H3.844c-.346 0-.625-.28-.625-.625zm8.31 3.233c.515-.516 1.35-.515 1.866 0l1.1 1.1c.516.516.516 1.351 0 1.866l-2.37 2.37c-.247.248-.582.387-.932.387H9.398c-.346 0-.625-.28-.625-.625v-1.795c0-.35.138-.685.386-.933l2.37-2.37zm.982.884c-.028-.028-.071-.027-.097-.001l-2.371 2.37c-.013.014-.02.031-.02.05v1.17h1.17c.018 0 .036-.007.049-.02l2.37-2.37c.028-.028.027-.072 0-.098L12.512 8.35zm-9.292-.646c0-.345.28-.625.625-.625H6.62c.345 0 .624.28.624.625s-.28.625-.624.625H3.844c-.346 0-.625-.28-.625-.625z"
                fill="currentColor"
                fillRule="evenodd"
              />
            </svg>
          </div>
        </ClickableDiv>
      )}
    </>
  );
};

export const UserInfoSkeleton = () => (
  <Layout
    avatarSlot={<Skeleton className="size-[80px] rounded-full" />}
    displayNameSlot={
      <TextSkeleton className="w-[100px]" fontSize={16} lineHeight={20} />
    }
    factionSlot={
      <TextSkeleton className="w-[60px]" fontSize={12} lineHeight={15} />
    }
  />
);
